import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

/**
 * ReportTitleSwitcher — button-styled H1 title with an inline chevron.
 *
 * Clicking the title opens a dropdown menu of all canned reports; selecting
 * one calls `onSwitch(reportId)`. Used inside PageHeader's `title` slot on
 * the canned-report detail page (RPT-1 State C, R8).
 *
 * Props:
 *   current    — { id, title, ... } current report config
 *   allReports — [{ value, label }] list of all reports available to switch to
 *   onSwitch   — fn(reportId) called when user picks a different report
 */
export default function ReportTitleSwitcher({ current, allReports = [], onSwitch }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const handleSelect = (id) => {
    setOpen(false);
    if (id !== current.id && typeof onSwitch === 'function') {
      onSwitch(id);
    }
  };

  const hasOptions = allReports.length > 1;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        aria-haspopup={hasOptions ? 'listbox' : undefined}
        aria-expanded={hasOptions ? open : undefined}
        onClick={() => hasOptions && setOpen((v) => !v)}
        className="inline-flex items-center gap-1"
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: hasOptions ? 'pointer' : 'default',
          font: 'inherit',
          color: 'var(--color-text-default)',
          fontSize: '1.5rem',
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        <span>{current.title}</span>
        {hasOptions && (
          <ChevronDownIcon
            className="h-5 w-5"
            aria-hidden="true"
            style={{
              transition: 'transform 0.15s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              color: 'var(--color-text-muted)',
            }}
          />
        )}
      </button>
      {open && hasOptions && (
        <ul
          role="listbox"
          aria-label="Switch report"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 20,
            minWidth: 260,
            maxHeight: 360,
            overflowY: 'auto',
            margin: 0,
            padding: '4px 0',
            listStyle: 'none',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {allReports.map((r) => {
            const isActive = r.value === current.id;
            return (
              <li key={r.value} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => handleSelect(r.value)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--neutral-50)]"
                  style={{
                    background: isActive ? 'var(--neutral-50)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-text-default)',
                    fontWeight: isActive ? 600 : 400,
                    display: 'block',
                  }}
                >
                  {r.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
